import { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { Category } from '../types';

interface SearchAndFiltersProps {
  search: string;
  selectedCategoryId?: string;
  onSearchChange: (search: string) => void;
  onCategoryChange: (categoryId: string | undefined) => void;
  onClearFilters: () => void;
}

interface CategoryWithCount extends Category {
  contactCount: number;
}

export function SearchAndFilters({
  search,
  selectedCategoryId,
  onSearchChange,
  onCategoryChange,
  onClearFilters,
}: SearchAndFiltersProps) {
  const [categoriesWithCount, setCategoriesWithCount] = useState<CategoryWithCount[]>([]);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [totalContacts, setTotalContacts] = useState(0);

  useEffect(() => {
    const fetchCategoriesWithCount = async () => {
      try {
        setLoadingCategories(true);
        const data = await apiService.getCategoriesWithCount();
        setCategoriesWithCount(data.categories);
        setUncategorizedCount(data.uncategorizedCount);
        
        // Calcular total de contatos
        const total = data.categories.reduce((sum, cat) => sum + cat.contactCount, 0) + data.uncategorizedCount;
        setTotalContacts(total);
      } catch (error) {
        console.error('Erro ao buscar categorias:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategoriesWithCount();
  }, []);

  return (
    <div className="flex flex-col sm:flex-row gap-4 mb-6">
      {/* Campo de busca */}
      <div className="flex-1">
        <label htmlFor="search" className="sr-only">
          Buscar contatos
        </label>
        <input
          id="search"
          type="text"
          placeholder="Buscar por nome, telefone ou email..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors text-sm font-normal"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-primary)'
          }}
          aria-label="Campo de busca para contatos"
        />
      </div>

      {/* Dropdown de categoria */}
      <div className="w-full sm:w-72">
        <label htmlFor="category-filter" className="sr-only">
          Filtrar por categoria
        </label>
        <select
          id="category-filter"
          value={selectedCategoryId || ''}
          onChange={(e) => onCategoryChange(e.target.value || undefined)}
          disabled={loadingCategories}
          className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors text-sm font-normal"
          style={{
            backgroundColor: 'var(--bg-card)',
            borderColor: 'var(--border-color)',
            color: 'var(--text-primary)'
          }}
          aria-label="Filtrar contatos por categoria"
        >
          <option value="">
            📂 Todas as categorias ({totalContacts})
          </option>
          
          {categoriesWithCount.map(cat => (
            <option key={cat.id} value={cat.id}>
              {cat.nome} ({cat.contactCount})
            </option>
          ))}
          
          {uncategorizedCount > 0 && (
            <option value="null">
              📭 Sem Categoria ({uncategorizedCount})
            </option>
          )}
        </select>
      </div>

      {/* Botão limpar */}
      {(search || selectedCategoryId) && (
        <button
          onClick={onClearFilters}
          className="px-4 py-2 hover:opacity-80 focus:outline-none focus:underline transition-opacity whitespace-nowrap text-sm font-normal"
          style={{ color: 'var(--text-secondary)' }}
          aria-label="Limpar filtros"
        >
          ✕ Limpar
        </button>
      )}
    </div>
  );
}
